import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import BookDetail from "@/pages/BookDetail";
import Questionnaire from "@/pages/Questionnaire";
import AdminPanel from "@/pages/AdminPanel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import BookPreview from "@/pages/BookPreview";
import { useEffect } from "react";
import { registerServiceWorker } from "@/lib/push";
import { initPixel, trackPageView } from "@/lib/pixel";

const Loader = () => (
  <div className="flex min-h-screen items-center justify-center bg-ink">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#D4AF37]" />
  </div>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/preview/:bookId" element={<BookPreview />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/books/:bookId"
        element={
          <ProtectedRoute>
            <BookDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/books/:bookId/questionario"
        element={
          <ProtectedRoute>
            <Questionnaire />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Register the PWA service worker as early as possible so push notifications
    // can arrive even when the site is not open.
    registerServiceWorker();
    // Load Meta Pixel once, then AppRoutes will fire PageView on each route change.
    initPixel();
  }, []);
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#0A1124",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
